import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@languages/index';
import API from 'api';
import { queries } from 'api/api.constants';
import { CAMPAIGN_DTO_ENUMS, getCampaignCommissionOptions } from 'api/repositories/campaign/campaign.constants';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import CampaignFormStepOne from '../campaignFormStepOne/campaignFormStepOne';
import CampaignFormStepTwo from '../campaignFormStepTwo/campaignFormStepTwo';
import CampaignFormStepThree from '../campaignFormStepThree/campaignFormStepThree';

const CreateCampaignForm = () => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const {
    modal,
    auth,
    brand,
    step,
    campaignFormMethods,
    campaignPaymentFormMethods,
    initializeCampaignFormData,
    setTemporaryKey,
    setShowLoader,
    getInitialCommissionData,
  } = useContext(CampaignFormContext);

  const defaultCampaignFormValues = useMemo(() => {
    const { commissionAmount: defaultCommissionAmount, commissionIncreaseMaxStep: defaultCommissionIncreaseMaxStep } =
      getInitialCommissionData(brand, undefined);
    return {
      outputTypeFl: brand.isCategoryBeauty
        ? CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.VIDEO
        : CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.PHOTO,
      brandManagerName: brand?.lastCampaign?.brandManagerName || '',
      brandManagerMobile: brand?.lastCampaign?.brandManagerMobile || `${languageTexts.mobile_left}-`,
      brandManagerCountryCode: brand?.lastCampaign?.brandManagerCountryCode || process.env.COUNTRY_CODE,
      returnManagerName: brand?.lastCampaign?.returnManagerName || '',
      returnManagerMobile: brand?.lastCampaign?.returnManagerMobile || `${languageTexts.mobile_left}-`,
      returnManagerCountryCode: brand?.lastCampaign?.returnManagerCountryCode || process.env.COUNTRY_CODE,
      commissionAmount: brand.isCategoryBeauty ? defaultCommissionAmount : null,
      commissionIncreaseMaxStep: brand.isCategoryBeauty ? defaultCommissionIncreaseMaxStep : null,
    };
  }, [brand]);

  const defaultCampaignPaymentFormValues = useMemo(() => {
    const { commissionPrice: defaultCommissionPrice } = getInitialCommissionData(brand, undefined);
    return {
      priceCommission: defaultCommissionPrice,
    };
  }, [brand]);

  const resetCampaignForm = useCallback(() => {
    campaignFormMethods.reset(defaultCampaignFormValues);
  }, [defaultCampaignFormValues]);

  const resetCampaignPaymentForm = useCallback(() => {
    campaignPaymentFormMethods.reset(defaultCampaignPaymentFormValues);
  }, [defaultCampaignPaymentFormValues]);

  // 임시 저장 캠페인 삭제
  const { mutate: clearTemporaryCampaignMutate } = useMutation({
    mutationFn: () =>
      API.Campaign.clearTemporaries({
        brandToken: auth.token,
      }),
    onSuccess: () => {
      resetCampaignForm();
      resetCampaignPaymentForm();
    },
    onError: (error: any) => {
      modal.showAlertModal({
        title: 'Error: clearTemporaryCampaigns',
        text: error.message,
      });
    },
  });

  // 임시 저장된 캠페인 불러오기
  const { data: temporaryCampaign } = useQuery({
    ...queries.campaign.getTemporaryCampaign({
      brandToken: auth.token,
      userTypeFl: GUIDELINE_RULE_DTO_ENUMS.USER_TYPE_FL.CAMPAIGN_NORMAL,
    }),
    enabled: !!auth.token,
  });

  // 임시 저장된 캠페인 fetching 후 데이터 처리
  useEffect(() => {
    if (temporaryCampaign?.data?.campaign) {
      const campaign = temporaryCampaign.data.campaign;
      // 브랜드 유형 불일치 - 일반 <-> 동대문
      if (campaign.brandBusinessType !== brand?.businessType) {
        clearTemporaryCampaignMutate();
      } else {
        let loadedCampaign = campaign;

        loadedCampaign['outputTypeFl'] = loadedCampaign?.outputTypeFl || defaultCampaignFormValues.outputTypeFl;
        loadedCampaign['brandManagerName'] = loadedCampaign?.brandManagerName || defaultCampaignFormValues.brandManagerName;
        loadedCampaign['brandManagerMobile'] = loadedCampaign?.brandManagerMobile || defaultCampaignFormValues.brandManagerMobile;
        loadedCampaign['brandManagerCountryCode'] = loadedCampaign?.brandManagerCountryCode || defaultCampaignFormValues.brandManagerCountryCode;
        loadedCampaign['returnManagerName'] = loadedCampaign?.returnManagerName || defaultCampaignFormValues.returnManagerName;
        loadedCampaign['returnManagerMobile'] = loadedCampaign?.returnManagerMobile || defaultCampaignFormValues.returnManagerMobile;
        loadedCampaign['returnManagerCountryCode'] = loadedCampaign?.returnManagerCountryCode || defaultCampaignFormValues.returnManagerCountryCode;

        // 뷰티 캠페인인 경우 추가 로직
        if (brand.isCategoryBeauty) {
          // 캠페인이 릴스가 아닌 경우 초기화(legacy)
          if (campaign.outputTypeFl !== CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.VIDEO) {
            clearTemporaryCampaignMutate();
            return;
          }

          // 원고료 기본값 설정
          const commissionOptions = getCampaignCommissionOptions(brand);
          const settableCommissionOption =
            commissionOptions.find((option) => {
              return option.commission === loadedCampaign?.commissionAmount;
            }) ??
            commissionOptions.find((option) => {
              return option.commission === defaultCampaignFormValues.commissionAmount;
            }) ??
            commissionOptions[1];
          loadedCampaign['commissionAmount'] = settableCommissionOption?.commission;
          loadedCampaign['commissionIncreaseMaxStep'] =
            loadedCampaign?.commissionIncreaseMaxStep == null
              ? settableCommissionOption?.maxStep ?? 0
              : Math.min(loadedCampaign?.commissionIncreaseMaxStep, settableCommissionOption?.maxStep ?? 0);
        }

        modal.showModal({
          hideCloseButton: true,
          title: languageTexts.campaign_form_load_temporary_campaign_modal_title,
          text: languageTexts.campaign_form_load_temporary_campaign_modal_text,
          confirmButtonText: languageTexts.campaign_form_load_temporary_campaign_modal_confirm,
          confirm: async () => {
            setTemporaryKey(loadedCampaign.temporaryKey);
            initializeCampaignFormData({ loadedCampaign });
          },
          cancel: () => {
            clearTemporaryCampaignMutate();
          },
        });
      }
    } else {
      resetCampaignForm();
      resetCampaignPaymentForm();
    }

    setShowLoader(false);
  }, [temporaryCampaign]);

  return (
    <>
      {step === 1 && <CampaignFormStepOne />}
      {step === 2 && <CampaignFormStepTwo />}
      {step === 3 && <CampaignFormStepThree />}
    </>
  );
};

export default CreateCampaignForm;
